import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash, X, CircleNotch, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function Ledgers({ cid }) {
  const [data, setData] = useState({ ledgers: [], groups: [] });
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", group: "Sundry Debtors", opening_balance: 0, opening_type: "dr", gstin: "", is_gst: false, gst_rate: 0, state: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/acc/${cid}/ledgers`); setData(r.data); } catch (e) { console.error(e); }
    setLoading(false);
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter ledger name"); return; }
    setSaving(true);
    try { await api.post(`/acc/${cid}/ledgers`, form); toast.success("Ledger saved"); setShow(false); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  const del = async (id) => { try { await api.delete(`/acc/${cid}/ledgers/${id}`); await load(); } catch { toast.error("Failed"); } };

  const byGroup = {};
  data.ledgers.forEach((l) => { (byGroup[l.group] = byGroup[l.group] || []).push(l); });

  return (
    <div data-testid="acc-ledgers">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#8C8381]">{data.ledgers.length} ledgers across your chart of accounts</p>
        <button data-testid="add-ledger-btn" onClick={() => { setForm({ name: "", group: "Sundry Debtors", opening_balance: 0, opening_type: "dr", gstin: "", is_gst: false, gst_rate: 0, state: "" }); setShow(true); }}
          className="flex items-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full px-4 py-2.5 transition-colors"><Plus size={16} weight="bold" /> Add ledger</button>
      </div>
      {loading ? <CircleNotch size={22} className="animate-spin text-[#F29A86]" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {(data.groups || []).filter((g) => byGroup[g.name]).map((g) => (
            <div key={g.name} className="bg-white border border-[#2A2625]/10 rounded-2xl overflow-hidden" data-testid={`ledger-group-${g.name}`}>
              <div className="px-4 py-2.5 bg-[#FCFAF8] border-b border-[#2A2625]/8 flex justify-between">
                <span className="text-sm font-semibold text-[#2A2625]">{g.name}</span>
                <span className="text-xs text-[#8C8381] uppercase">{g.nature}</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {byGroup[g.name].map((l) => (
                    <tr key={l.ledger_id} className="border-t border-[#2A2625]/6" data-testid={`ledger-row-${l.ledger_id}`}>
                      <td className="px-4 py-2 font-medium">{l.name}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">{inr(l.balance?.closing != null ? Math.abs(l.balance.closing) : 0)} {l.balance?.side}</td>
                      <td className="px-3 py-2 text-right"><button onClick={() => del(l.ledger_id)} className="text-[#8C8381] hover:text-[#E09494]"><Trash size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {show && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setShow(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="ledger-modal">
            <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-semibold">Add ledger</h3><button onClick={() => setShow(false)}><X size={18} className="text-[#8C8381]" /></button></div>
            <div className="space-y-3">
              <input data-testid="ledger-name" placeholder="Ledger name (e.g. ABC Traders)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
              <select data-testid="ledger-group" value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} className="w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]">
                {(data.groups || []).map((g) => <option key={g.name} value={g.name}>{g.name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="ledger-opening" type="number" placeholder="Opening balance" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: parseFloat(e.target.value) || 0 })} className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
                <select data-testid="ledger-optype" value={form.opening_type} onChange={(e) => setForm({ ...form, opening_type: e.target.value })} className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]">
                  <option value="dr">Debit</option><option value="cr">Credit</option>
                </select>
              </div>
              <input data-testid="ledger-gstin" placeholder="Party GSTIN (optional)" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} className="w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
            </div>
            <button data-testid="save-ledger-btn" onClick={save} disabled={saving} className="mt-5 w-full flex items-center justify-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full px-5 py-2.5 disabled:opacity-60">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />} Save ledger</button>
          </div>
        </div>
      )}
    </div>
  );
}
